import { useState } from 'react';
import { TextKey } from '@mews-commander/enums';
import { PayoutDetailResult, PayoutProcessingType } from '@mews-commander/data-access';
import { StandaloneTable as Table, useTableState } from '@optimus-web/core';
import { useExportPayoutDetail, useGetPayoutDetailSummary, useGetPayoutTransactions } from '../queries';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../constants';
import { useGetBreadcrumbs } from '../hooks';
import { PayoutReportDetailSummary } from './PayoutReportDetailSummary';
import { getPayoutDetailColumns } from './tableMapping';
import { PayoutReportLayout } from './PayoutReportLayout';
import { PaymentDetailSidePanel } from './paymentDetailSidePanel/PaymentDetailSidePanel';
import { PayoutReportDetailBanner } from './PayoutReportDetailBanner';

export const PayoutReportDetail = ({ id, guideLink }: { id: string; guideLink: string }) => {
    const [tableState, setTableState] = useTableState<PayoutDetailResult>({
        pagination: {
            pageSize: DEFAULT_PAGE_SIZE,
            pageIndex: DEFAULT_PAGE_INDEX,
        },
    });

    const {
        data: payoutDetailSummary,
        isFetching: isPayoutSummaryDataFetching,
        isError: hasPayoutSummaryDataFailed,
    } = useGetPayoutDetailSummary({
        payoutId: id,
    });

    const {
        data: payoutTransactions = { data: [], totalRowCount: 0 },
        isFetching: isPayoutTransactionsDataFetching,
        isError: hasPayoutTransactionsDataFailed,
    } = useGetPayoutTransactions({ ...tableState, payoutId: id });

    const breadcrumbs = useGetBreadcrumbs({ headerTextKey: TextKey.PayoutDetail });

    const [sidePanelOpen, setSidePanelOpen] = useState(false);

    const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');

    const openSidePanel = (paymentId: string) => {
        setSelectedPaymentId(paymentId);
        setSidePanelOpen(true);
    };

    const closeSidePanel = () => {
        setSelectedPaymentId('');
        setSidePanelOpen(false);
    };

    const exportPayout = useExportPayoutDetail();

    const handleExport = () => exportPayout.mutate({ payoutId: id });

    return (
        <PayoutReportLayout
            exportHandler={handleExport}
            breadcrumbs={breadcrumbs}
            hasFailed={hasPayoutSummaryDataFailed || hasPayoutTransactionsDataFailed}
            isFetching={isPayoutSummaryDataFetching}
            guideLink={guideLink}
            summary={
                payoutDetailSummary ? <PayoutReportDetailSummary payoutDetailSummary={payoutDetailSummary} /> : null
            }
            banner={
                payoutDetailSummary?.payoutProcessingType === PayoutProcessingType.StripeManual ? (
                    <PayoutReportDetailBanner payoutDetailSummary={payoutDetailSummary} />
                ) : null
            }
        >
            <Table
                data={payoutTransactions.data}
                uniqueRowId="id"
                loading={isPayoutTransactionsDataFetching}
                columns={getPayoutDetailColumns({
                    openSidePanel,
                })}
                amountOfRows={payoutTransactions.totalRowCount}
                state={tableState}
                onStateChange={setTableState}
            />
            {selectedPaymentId && (
                <PaymentDetailSidePanel
                    isSidePanelOpen={sidePanelOpen}
                    closeSidePanel={closeSidePanel}
                    selectedPaymentId={selectedPaymentId}
                />
            )}
        </PayoutReportLayout>
    );
};
